import { DocumentIcon } from '@/assets/icons'
import Layout from '@/components/layout/Layout'
import { MTKeyFilter } from '@/multi-territory/mt-components/mt-returns/MTKeyFilter'
import { MTReturnsWorkflowSteps } from '@/multi-territory/mt-components/mt-returns/MTReturnsWorkflowSteps'
import { MTIconMap } from '@/multi-territory/mt-constants/mt-icon-map'
import { MTLabelMap } from '@/multi-territory/mt-constants/mt-label-map'
import { MTRouteMap } from '@/multi-territory/mt-constants/mt-route-map'
import {
  MTReturnsStepConfig,
  MTSubModule,
} from '@/multi-territory/mt-types/mt-returns'
import { MTIconProps, MTSidebarSubModule } from '@/multi-territory/mt-types/mt-shared'
import ROUTES from '@/routes/routeConfig'
import { Box, Typography } from '@mui/material'
import { ComponentType, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router'

// Fallback steps (used while navigation API loads or on failure)
const FALLBACK_STEPS: MTReturnsStepConfig[] = [
  { key: 'define', label: 'Define', icon: MTIconMap.define as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_DEFINE, subModules: [] },
  { key: 'upload', label: 'Upload', icon: MTIconMap.upload as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_UPLOAD, subModules: [] },
  { key: 'prepare', label: 'Prepare', icon: MTIconMap.prepare as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_PREPARE, subModules: [] },
  { key: 'compute', label: 'Compute', icon: MTIconMap.compute as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_COMPUTE, subModules: [] },
  { key: 'allocate', label: 'Allocate', icon: MTIconMap.allocate as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_ALLOCATE, subModules: [] },
  { key: 'returns', label: 'Returns', icon: MTIconMap.returns as ComponentType<MTIconProps>, route: ROUTES.MT_RETURNS_RETURNS, subModules: [] },
]

/**
 * Converts navigation API subModules into workflow step configs.
 * The navigation API returns RETURNS module with subModules like:
 * { code: "define", label: "Define", order: 1, route: "/returns/define", subModules: [...] }
 */
const resolveStepsFromNav = (subModules: MTSidebarSubModule[]): MTReturnsStepConfig[] => {
  return [...subModules]
    .sort((a, b) => a.order - b.order)
    .map(sub => {
      const icon: ComponentType<MTIconProps> =
        (MTIconMap[sub.code] as ComponentType<MTIconProps>) ??
        (DocumentIcon as ComponentType<MTIconProps>)

      const label: string = MTLabelMap[sub.code] ?? sub.label
      const route: string = MTRouteMap[sub.code] ?? `${ROUTES.MT_RETURNS}/${sub.code}`

      // Convert nested subModules to MTSubModule format for MTKeyFilter.
      // The Prepare step must not render its sub-module tab bar (e.g. "Rate
      // Determination"), so we intentionally drop its sub-modules here.
      const childSubModules: MTSubModule[] =
        sub.code === 'prepare'
          ? []
          : (sub.subModules || []).map(child => ({
              code: child.code,
              label: child.label,
              order: child.order,
              enabled: child.enabled !== false,
            }))

      return { key: sub.code, icon, label, route, subModules: childSubModules }
    })
}

export function MTReturns() {
  const location = useLocation()

  // Get navigation modules from Redux (set by Layout via navigation API)
  const { navModules } = useSelector((state: any) => state.navigation)
  const countryName = useSelector((state: any) => state.country?.countryName || '')

  // Find the RETURNS module from navigation data and extract its subModules as steps
  const steps = useMemo(() => {
    if (!navModules) return FALLBACK_STEPS

    const returnsModule = navModules.find((mod: any) => mod.code === 'RETURNS')
    if (!returnsModule || !returnsModule.subModules || returnsModule.subModules.length === 0) {
      return FALLBACK_STEPS
    }

    return resolveStepsFromNav(returnsModule.subModules)
  }, [navModules])

  // Determine the currently active parent step based on the URL
  const activeStep = useMemo(() => {
    return steps.find(step => location.pathname.startsWith(step.route))
  }, [steps, location.pathname])

  // Get sub-modules for the active step
  const activeSubModules: MTSubModule[] = activeStep?.subModules ?? []

  return (
    <Layout>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid var(--border-strong)',
          px: 2,
          pb: 0,
          mt: 2,
        }}
      >
        {/* Country label on the left per Figma */}
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            color: 'var(--forbrand-secondary)',
            whiteSpace: 'nowrap',
            mr: 3,
            lineHeight:'140%'
          }}
        >
          Country:{' '}
          <Typography
            component="span"
            sx={{
              fontSize: '16px',
              fontWeight: 400,
              color: 'var(--forbrand-primary)',
              lineHeight:'140%'
            }}
          >
            {countryName}
          </Typography>
        </Typography>
        <MTReturnsWorkflowSteps steps={steps} />
      </Box>

      {/* Sub-module tabs for the active parent module */}
      {activeSubModules.length > 0 && activeStep && (
        <MTKeyFilter
          subModules={activeSubModules}
          baseRoute={activeStep.route}
        />
      )}

      <Outlet />
    </Layout>
  )
}
